import { getSubgenreOptions } from "./selectUtils";
import { DSPPlatform } from "../types/dropdownTypes";

export const NONE_OF_THESE = "None Of These";

/**
 * Splits a saved comma-separated answer into its options, keeping an option
 * that contains a comma ("Work, Study, & Reading") in one piece.
 */
export const splitAnswer = (value: string | null | undefined, options: readonly string[]): string[] => {
  const pieces = (value || "").split(",").map((piece) => piece.trim()).filter(Boolean);
  const withCommas = options
    .filter((option) => option.includes(","))
    .map((option) => option.split(",").map((part) => part.trim().toLowerCase()))
    .sort((a, b) => b.length - a.length);
  const answers: string[] = [];
  for (let i = 0; i < pieces.length; ) {
    const parts = withCommas.find((candidate) =>
      candidate.every((part, k) => i + k < pieces.length && pieces[i + k].toLowerCase() === part),
    );
    const length = parts ? parts.length : 1;
    answers.push(pieces.slice(i, i + length).join(", "));
    i += length;
  }
  return answers;
};

/**
 * "None Of These" can't be combined with another mood: picking it clears the
 * other moods, and picking another mood clears it.
 */
export const exclusiveMoods = (previous: string[], next: string[]): string[] => {
  if (next.length < 2 || !next.includes(NONE_OF_THESE)) return next;
  return previous.includes(NONE_OF_THESE)
    ? next.filter((mood) => mood !== NONE_OF_THESE)
    : [NONE_OF_THESE];
};

// Form fields holding each DSP's subgenres, which are listed per main genre.
export const SUBGENRE_FIELDS: ReadonlyArray<[DSPPlatform, SubgenreField]> = [
  ["spotify", "spotifySubgenre"],
  ["amazon", "amazonSubgenre"],
  ["deezer", "deezerSubgenre"],
  ["audiomack", "audiomackSubgenre"],
  ["TIDAL", "TIDALSubgenre"],
  ["soundcloud", "soundCloudSubgenre"],
];

export type SubgenreField =
  | "spotifySubgenre"
  | "amazonSubgenre"
  | "deezerSubgenre"
  | "audiomackSubgenre"
  | "TIDALSubgenre"
  | "soundCloudSubgenre";

/**
 * The subgenre picks a main-genre change takes away: offered under the old
 * main genres but not under the new ones. The backend refuses a subgenre that
 * isn't under the pitch's main genres, so these are cleared. Other values,
 * such as one taken off a list that an older pitch still has, are left alone.
 * Returns each field to update with the values it keeps.
 */
export const subgenresAfterGenreChange = (
  values: Partial<Record<SubgenreField, string[]>>,
  previousGenres: string[],
  nextGenres: string[],
): Array<[SubgenreField, string[]]> => {
  const updates: Array<[SubgenreField, string[]]> = [];
  for (const [dsp, field] of SUBGENRE_FIELDS) {
    const picked = values[field] || [];
    if (!picked.length) continue;
    const before = getSubgenreOptions(dsp, previousGenres);
    const after = getSubgenreOptions(dsp, nextGenres);
    const kept = picked.filter((value) => after.includes(value) || !before.includes(value));
    if (kept.length !== picked.length) updates.push([field, kept]);
  }
  return updates;
};
